import React from 'react';
import PropTypes from 'prop-types';
import { ContactGroup, SidebarHeading } from '@massds/mayflower-react';
import './index.scss';

/**
 * Formats a phone number of just digits into (XXX) XXX-XXXX.
 * @param {string} phoneNumberString - A phone number string of just digits (XXXXXXXXXX).
 * @returns {string or null} - If the passed string is 10 digits, it returns a formatted
    phone number. If the string is not 10 digits, returns null.
 */
const formatPhoneNumber = (phoneNumberString) => {
  const cleaned = (phoneNumberString).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return`(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return null;
};

const ContactInformation = (props) => {
  const { phone, online, address } = props;
  return(
    <div className="sidebar__contact">
      <SidebarHeading level={2} title="Contact" />
      <ContactGroup
        icon="SvgPhone"
        name="Phone"
        level={3}
        items={phone.map((item) => ({
          type: 'phone',
          label: item.title,
          link: {
            text: formatPhoneNumber(item.number),
            href: item.number
          },
          details: item.details
        }))}
      />
      <ContactGroup
        icon="SvgLaptop"
        name="Online"
        level={3}
        items={online.map((item) => ({
          type: 'email',
          label: item.title,
          link: {
            text: item.email,
            href: item.email
          },
          details: item.details
        }))}
      />
      <ContactGroup
        icon="SvgMarker"
        name="Address"
        level={3}
        items={address.map((item) => ({
          type: 'address',
          label: item.title,
          address: item.address,
          link: {
            text: 'directions',
            href: item.directions
          },
          details: item.details
        }))}
      />
    </div>
  );
};

ContactInformation.propTypes = {
  /** an array of distinct phone contact information objects * */
  phone: PropTypes.arrayOf(PropTypes.shape({
    /** the phone number label/title (e.g. Governor's Office) * */
    title: PropTypes.string,
    /** the phone number, as plain digits without formatting (e.g. 6171231234) * */
    number: PropTypes.string,
    /** any additional details you want displayed beneath the phone number, such hours of operation * */
    details: PropTypes.string
  })),
  /** an array of distinct email and related contact information objects * */
  online: PropTypes.arrayOf(PropTypes.shape({
    /** the label/title of the email (e.g. Constituent Services) * */
    title: PropTypes.string,
    /** the email address * */
    email: PropTypes.string,
    /** any further details you displayed beneath the email * */
    details: PropTypes.string
  })),
  /** an array of distinct address information objects * */
  address: PropTypes.arrayOf(PropTypes.shape({
    /** the name of the address location (e.g. State House) * */
    title: PropTypes.string,
    /** the full address * */
    address: PropTypes.string,
    /** a link to a prepopulated directions url (https://maps.google.com/?q=Massachusetts+State+House%2C+24+Beacon+St.%2C+Office+of+the+Governor%2C+Room+280%2C+Boston%2C+MA+02133) * */
    directions: PropTypes.string,
    /** an further details you want displayed beneath the address (e.g. hours of operation) * */
    details: PropTypes.string
  }))
};

export default ContactInformation;
